import React, { useEffect, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl';
import LoadingSpinner from '../ui/LoadingSpinner';

// Use the same token as the main map component
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

interface AddressMapProps {
  /** Full address string to geocode and display */
  address: string;
  /** Optional height for the map container */
  height?: string;
  /** Optional CSS class for styling */
  className?: string;
  /** Customer name for the marker popup */
  customerName?: string;
}

export const AddressMap: React.FC<AddressMapProps> = ({ 
  address, 
  height = '300px', 
  className = '',
  customerName 
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // Initialize map
  useEffect(() => {
    if (mapRef.current || !containerRef.current) {return;}

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [-1.5597348, 52.3887418], // Default to UK center
      zoom: 10,
    });

    mapRef.current = map;

    map.on('load', () => {
      map.addControl(new mapboxgl.NavigationControl());
      setReady(true);
    });

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      setReady(false);
    };
  }, []);

  // Geocode address and add marker
  useEffect(() => {
    if (!ready || !mapRef.current || !address?.trim()) {
      setLoading(false);
      return;
    }

    const geocodeAndShowAddress = async () => {
      setLoading(true);
      setError(null);

      try {
        const query = encodeURIComponent(address.trim());
        const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?access_token=${mapboxgl.accessToken}&limit=1`;
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Geocoding failed: ${response.status}`);
        }

        const data = await response.json();
        const feature = data?.features?.[0];

        if (!feature?.center?.length) {
          throw new Error('Address not found');
        }

        const [lng, lat] = feature.center;

        // Remove existing marker
        if (markerRef.current) {
          markerRef.current.remove();
        }

        // Create marker element
        const markerEl = document.createElement('div');
        markerEl.className = 'marker se-icon--building-marker';
        markerEl.style.cursor = 'pointer';

        // Create popup content
        const popupHtml = `
          <div class="map-popup">
            <div class="map-popup__header">
              <h4 class="map-popup__title">${customerName || 'Customer Location'}</h4>
            </div>
            <div class="map-popup__body">
              <div class="map-popup__row">
                <span class="map-popup__label">Address</span>
                <span class="map-popup__value">${address}</span>
              </div>
            </div>
          </div>
        `;

        const popup = new mapboxgl.Popup({ 
          offset: 25,
          closeButton: true 
        }).setHTML(popupHtml);

        // Create and add marker
        const marker = new mapboxgl.Marker(markerEl)
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(mapRef.current!);

        markerRef.current = marker;

        // Center map on the address
        mapRef.current!.flyTo({
          center: [lng, lat],
          zoom: 15,
          duration: 1000
        });

      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load address location');
      } finally {
        setLoading(false);
      }
    };

    void geocodeAndShowAddress();
  }, [ready, address, customerName]);

  const containerStyle: React.CSSProperties = {
    height,
    width: '100%',
    position: 'relative',
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden'
  };

  return (
    <div className={`address-map ${className}`} style={containerStyle}>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
      
      {loading && (
        <div style={{ position: 'absolute', top: '8px', left: '8px' }}>
          <LoadingSpinner message="Loading map..." />
        </div>
      )}
      
      {error && (
        <div style={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          background: '#faa',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)'
        }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default AddressMap;